import type { KeyedMutator } from 'swr';

import useLabelsAlbumsOnMedia from '@/utils/hooks/utils/useLabelsAlbumsOnMedia';

// Lib
import {
    ADD_ERROR,
    ADD_SUCCESS,
    DELETE_ERROR,
    DELETE_FROM_MEDIA_ERROR,
    DELETE_FROM_MEDIA_SUCCESS,
    DELETE_SUCCESS
} from '@/lib/posts/postLabelsAlbumsEvents';

type MediaType = {
    id: string;
    labels: {
        label: string;
    }[];
    albums: {
        id: number;
        count_medias: number;
        name: string;
        type: 'auto' | 'manual';
    }[];
};

type EventType =
    | typeof ADD_SUCCESS
    | typeof ADD_ERROR
    | typeof DELETE_FROM_MEDIA_SUCCESS
    | typeof DELETE_FROM_MEDIA_ERROR
    | typeof DELETE_SUCCESS
    | typeof DELETE_ERROR;

type useLabelsAlbumsOnMediaViewerType = {
    mutateMedias: KeyedMutator<Record<'medias' | 'stories', MediaType[]>[]>;
    mutateMedia: KeyedMutator<{ media: MediaType }>;
    type?: 'media' | 'story';
};

export const useLabelsAlbumsOnMediaViewer = ({
    mutateMedias,
    mutateMedia,
    type
}: useLabelsAlbumsOnMediaViewerType) => {
    const { handleAlbumsEvent: handleAlbumsAllMedia, handleLabelsEvent: handleLabelsAllMedia } =
        useLabelsAlbumsOnMedia({
            mutateMedias,
            type: type === 'story' ? 'stories' : 'medias'
        });
    // Function that handle the cache mutation of the medias based on label actions
    const handleLabelsEvent = (data, event: EventType, mediaId: string) => {
        switch (event) {
            case ADD_SUCCESS:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            labels: [...prevData[type].labels, data.slice(-1)[0]]
                        }
                    }),

                    false
                );
                handleLabelsAllMedia(data, ADD_SUCCESS, mediaId);
                break;
            case ADD_ERROR:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            labels: prevData[type].labels?.filter(
                                (el) => el?.label !== data.slice(-1)[0]?.label
                            )
                        }
                    }),
                    false
                );
                handleLabelsAllMedia(data, ADD_ERROR, mediaId);
                break;
            case DELETE_FROM_MEDIA_SUCCESS:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            labels: prevData[type].labels?.filter((el) => el.label !== data.label)
                        }
                    }),
                    false
                );
                handleLabelsAllMedia(data, DELETE_FROM_MEDIA_SUCCESS, mediaId);
                break;
            case DELETE_SUCCESS:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            labels: prevData[type].labels?.filter((el) => el.label !== data.label)
                        }
                    }),
                    false
                );
                handleLabelsAllMedia(data, DELETE_SUCCESS, mediaId);
                break;
            case DELETE_FROM_MEDIA_ERROR:
            case DELETE_ERROR:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            labels: prevData[type].labels.some((el) => el.label === data.label)
                                ? prevData[type].labels
                                : [
                                      ...prevData[type].labels,
                                      { label: data.label, value: data.label, id: data.label }
                                  ]
                        }
                    }),
                    false
                );
                handleLabelsAllMedia(data, DELETE_FROM_MEDIA_ERROR, mediaId);
                break;
            default:
                return null;
        }
    };

    // Function that handle the cache mutation of the medias based on albums actions
    const handleAlbumsEvent = (dataAlbum, event: EventType, mediaId: string) => {
        switch (event) {
            case ADD_SUCCESS:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            albums: [...prevData[type].albums, dataAlbum]
                        }
                    }),
                    false
                );
                handleAlbumsAllMedia(dataAlbum, ADD_SUCCESS, mediaId);
                break;
            case ADD_ERROR:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            albums: prevData[type].albums?.filter((el) => el?.id !== dataAlbum.id)
                        }
                    }),
                    false
                );
                handleAlbumsAllMedia(dataAlbum, ADD_ERROR, mediaId);
                break;
            case DELETE_SUCCESS:
                mutateMedia(
                    (prevData) => ({
                        ...prevData,
                        [type]: {
                            ...prevData[type],
                            albums: prevData[type].albums?.filter((el) => el.id !== dataAlbum.id)
                        }
                    }),
                    false
                );
                handleAlbumsAllMedia(dataAlbum, DELETE_SUCCESS, mediaId, true);
                break;
            default:
                return null;
        }
    };

    return { handleAlbumsEvent, handleLabelsEvent };
};
